import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import HandleHttpError from '@/shared/services/HandleHttpError';
import debounce from 'lodash-es/debounce';
import api from '@/shared/api';
import config from '@/config';
import classNames from 'classnames';
import { PaperclipIcon } from '@/shared/components/Icons';
import PopoverStickOnHover from '@/shared/components/PopoverStickOnHover';
import TaskAttachmentsPopover from './TaskAttachmentsPopover';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import intlShape from '@/shared/utils/intlShape';

const retrieveAttachmentsForTaskIds = taskIds =>
  api.base.post(`${config.appUrls.documents}/api/documents/DocumentLinks`, taskIds);
const retrieveAttachmentsForTask = (taskLevel, taskId) => {
  // if hovered over a main task, we'll retrieve the subtask IDs and check which documents are linked to them as well
  if (taskLevel === 0) {
    return api.base.get(`${config.appUrls.tasks}/api/plannedtasks/ChildTaskIds/${taskId}`).then(
      ({ data }) => {
        return retrieveAttachmentsForTaskIds([taskId, ...data]);
      },
      () => retrieveAttachmentsForTaskIds([taskId]),
    );
  } else {
    return retrieveAttachmentsForTaskIds([taskId]);
  }
};

const TaskAttachmentIcon = ({ hasAttachments, taskId, taskLevel, onOpenDetail, intl }) => {
  const [attachments, setAttachments] = useState(null);
  const [loading, setLoading] = useState(false);

  if (typeof hasAttachments === 'undefined') return null;

  const debouncedRetrieveAttachments = useCallback(
    debounce(() => {
      retrieveAttachmentsForTask(taskLevel, taskId)
        .then(({ data }) => {
          setAttachments(data);
          setLoading(false);
        })
        .catch(error => {
          HandleHttpError(intl)(error);
          setLoading(false);
        });
    }, 300),
    [],
  );

  const retrieveAttachments = () => {
    if (attachments) return;
    setLoading(true);
    setAttachments(null);

    debouncedRetrieveAttachments();
  };

  return hasAttachments ? (
    <div className="task-status-icon visible">
      <span
        onClick={e => {
          e.stopPropagation();
        }}
      >
        <PopoverStickOnHover
          trigger={['hover', 'focus']}
          placement="top"
          component={
            <TaskAttachmentsPopover attachments={attachments} loadingAttachments={loading} />
          }
          shouldUpdatePosition
          onMouseEnter={retrieveAttachments}
          onClick={() => (onOpenDetail ? onOpenDetail(taskId) : {})}
        >
          <span>
            <PaperclipIcon />
          </span>
        </PopoverStickOnHover>
      </span>
    </div>
  ) : (
    <div className="task-status-icon">
      <span className="empty-react-icon"></span>
    </div>
  );
};
TaskAttachmentIcon.propTypes = {
  taskId: PropTypes.string.isRequired,
  taskLevel: PropTypes.number.isRequired,
  hasAssignments: PropTypes.bool,
  onOpenDetail: PropTypes.func,
  intl: intlShape,
};
TaskAttachmentIcon.defaultProps = {
  hasAssignments: undefined,
  onOpenDetail: undefined,
};
export default TaskAttachmentIcon;
